// src/services/api_secure.js
import axios from 'axios';

// Use empty string to make requests relative to current domain (nginx will proxy /api to backend)
const API_URL = process.env.REACT_APP_API_URL || '';

// Helper function to get CSRF token from cookies
function getCSRFToken() {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return decodeURIComponent(value);
    }
  }
  return null;
}

// Helper function to check if we have an access token cookie
function hasAccessToken() {
  return document.cookie.includes('access_token=');
}

// Create axios instance with secure configuration
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Always send cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track token refresh promise to prevent multiple simultaneous refreshes
let refreshPromise = null;

// Add request interceptor for CSRF token and error handling
api.interceptors.request.use(
  (config) => {
    // Add CSRF token to state-changing requests
    if (['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // If httpOnly cookie isn't available, fall back to legacy token
    if (!hasAccessToken()) {
      const legacyToken = localStorage.getItem('jmc_token');
      if (legacyToken) {
        config.headers.Authorization = `Bearer ${legacyToken}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for token refresh and error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle rate limiting
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || '60';
      const message = `Too many requests. Please try again in ${retryAfter} seconds.`;

      // Dispatch custom event for UI handling
      window.dispatchEvent(new CustomEvent('rateLimited', {
        detail: { retryAfter, message }
      }));

      return Promise.reject({
        ...error,
        rateLimited: true,
        retryAfter: parseInt(retryAfter),
        message
      });
    }

    // Handle token expiration (401) - attempt refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If already refreshing, wait for it
      if (refreshPromise) {
        await refreshPromise;
        return api(originalRequest);
      }

      // Start refresh process
      refreshPromise = authService.refreshToken()
        .then(() => {
          refreshPromise = null;
          return api(originalRequest);
        })
        .catch((refreshError) => {
          refreshPromise = null;
          // Refresh failed, redirect to login
          authService.logout();
          window.location.href = '/';
          return Promise.reject(refreshError);
        });

      return refreshPromise;
    }

    // Handle account locked (403)
    if (error.response?.status === 403) {
      const message = error.response.data?.detail || 'Account locked or access denied';

      if (message.includes('locked')) {
        window.dispatchEvent(new CustomEvent('accountLocked', {
          detail: { message }
        }));
      }
    }

    return Promise.reject(error);
  }
);

// Authentication services with enhanced security
export const authService = {
  login: async (email, password) => {
    console.log('=== API Login Started ===');
    try {
      // Clear any legacy tokens first
      console.log('1. Clearing old tokens...');
      localStorage.removeItem('jmc_token');
      localStorage.removeItem('jmc_user');

      console.log('2. Sending login request to:', '/api/login');
      const response = await api.post('/api/login', { email, password });
      console.log('3. Login response received:', response.data);

      // Store user info (but not token - that's in httpOnly cookie now)
      if (response.data) {
        // Parse JWT to get user info (if needed)
        try {
          console.log('4. Parsing access token...');
          const tokenParts = response.data.access_token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('5. Token payload:', payload);

            const userInfo = {
              email: payload.sub || email,
              role: response.data.role || payload.role,
              first_name: payload.first_name,
              last_name: payload.last_name
            };

            console.log('6. Storing user info in localStorage:', userInfo);
            localStorage.setItem('jmc_user', JSON.stringify(userInfo));

            // Also store access token for backward compatibility with existing components
            localStorage.setItem('jmc_token', response.data.access_token);
            console.log('6b. Stored access token for backward compatibility');

            // Verify it was stored
            const stored = localStorage.getItem('jmc_user');
            console.log('7. Verification - localStorage now contains:', stored);
          }
        } catch (e) {
          console.error('Error parsing token, using fallback:', e);
          // Fallback to basic user info
          const fallbackInfo = {
            email: email,
            role: response.data.role || 'user'
          };
          console.log('6b. Storing fallback user info:', fallbackInfo);
          localStorage.setItem('jmc_user', JSON.stringify(fallbackInfo));
          localStorage.setItem('jmc_token', response.data.access_token);
        }
      }

      console.log('8. Login complete, returning response');
      console.log('=== API Login Complete ===\n');
      return response.data;
    } catch (error) {
      if (error.rateLimited) {
        throw error;
      }
      throw error.response ? error.response.data : { detail: 'Network error' };
    }
  },

  signup: async (email, password, confirmPassword, firstName = null, lastName = null) => {
    try {
      // Clear any legacy tokens first
      localStorage.removeItem('jmc_token');
      localStorage.removeItem('jmc_user');

      const response = await api.post('/api/signup', {
        email,
        password,
        confirm_password: confirmPassword,
        first_name: firstName,
        last_name: lastName
      });

      // Store user info
      if (response.data) {
        localStorage.setItem('jmc_user', JSON.stringify({
          email: email,
          role: response.data.role || 'user',
          first_name: firstName,
          last_name: lastName
        }));
        localStorage.setItem('jmc_token', response.data.access_token);
      }

      return response.data;
    } catch (error) {
      if (error.rateLimited) {
        throw error;
      }
      throw error.response ? error.response.data : { detail: 'Network error' };
    }
  },

  logout: async () => {
    try {
      // Call logout endpoint to clear server-side session
      await api.post('/api/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('jmc_user');
      localStorage.removeItem('jmc_token'); // Remove legacy token

      // Clear cookies manually as backup
      document.cookie = 'access_token=; Max-Age=0; path=/;';
      document.cookie = 'refresh_token=; Max-Age=0; path=/;';
      document.cookie = 'csrf_token=; Max-Age=0; path=/;';
    }
  },

  refreshToken: async () => {
    try {
      const response = await api.post('/api/refresh', {});
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { detail: 'Token refresh failed' };
    }
  },

  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await api.post('/api/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { detail: 'Password change failed' };
    }
  },

  requestPasswordReset: async (email) => {
    try {
      const response = await api.post('/api/password-reset/request', { email });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { detail: 'Password reset request failed' };
    }
  },

  confirmPasswordReset: async (token, newPassword, confirmPassword) => {
    try {
      const response = await api.post('/api/password-reset/confirm', {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { detail: 'Password reset failed' };
    }
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('jmc_user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  },

  isAuthenticated: () => {
    // Check for user info in localStorage as primary indicator
    // (cookies are httpOnly so we can't read them directly)
    const userInfo = localStorage.getItem('jmc_user');

    // Also check for legacy token for backward compatibility
    const legacyToken = localStorage.getItem('jmc_token');

    return !!userInfo || !!legacyToken;
  },

  // Validate password on client side (for immediate feedback)
  validatePassword: (password, email = null) => {
    const errors = [];

    // Length check
    if (password.length < 12) {
      errors.push('Password must be at least 12 characters long');
    }
    if (password.length > 128) {
      errors.push('Password must not exceed 128 characters');
    }

    // Complexity checks
    if (!/[A-Z]/.test(password)) {
      errors.push('Must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Must contain at least one number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};:'",.<>/?\\|`~]/.test(password)) {
      errors.push('Must contain at least one special character');
    }

    // Common password check
    const commonPasswords = ['password', '123456', 'password123', 'admin', 'letmein'];
    if (commonPasswords.includes(password.toLowerCase())) {
      errors.push('This password is too common');
    }

    // Check for email username in password
    if (email) {
      const username = email.split('@')[0].toLowerCase();
      if (username.length > 3 && password.toLowerCase().includes(username)) {
        errors.push('Password should not contain your email username');
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
      strength: calculatePasswordStrength(password)
    };
  }
};

// Calculate password strength (0-100)
function calculatePasswordStrength(password) {
  let strength = 0;

  // Length bonus
  strength += Math.min(password.length * 2, 30);

  // Complexity bonus
  if (/[a-z]/.test(password)) strength += 10;
  if (/[A-Z]/.test(password)) strength += 10;
  if (/\d/.test(password)) strength += 10;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 20;

  // Variety bonus
  const uniqueChars = new Set(password).size;
  strength += Math.min(uniqueChars, 20);

  return Math.min(strength, 100);
}

// Export rate limit handler for UI components
export const rateLimitHandler = {
  listeners: new Set(),

  addListener: (callback) => {
    const handler = (event) => callback(event.detail);
    window.addEventListener('rateLimited', handler);
    rateLimitHandler.listeners.add(handler);
    return handler;
  },

  removeListener: (handler) => {
    window.removeEventListener('rateLimited', handler);
    rateLimitHandler.listeners.delete(handler);
  },

  removeAllListeners: () => {
    rateLimitHandler.listeners.forEach(handler => {
      window.removeEventListener('rateLimited', handler);
    });
    rateLimitHandler.listeners.clear();
  }
};

// Export account lock handler for UI components
export const accountLockHandler = {
  listeners: new Set(),

  addListener: (callback) => {
    const handler = (event) => callback(event.detail);
    window.addEventListener('accountLocked', handler);
    accountLockHandler.listeners.add(handler);
    return handler;
  },

  removeListener: (handler) => {
    window.removeEventListener('accountLocked', handler);
    accountLockHandler.listeners.delete(handler);
  },

  removeAllListeners: () => {
    accountLockHandler.listeners.forEach(handler => {
      window.removeEventListener('accountLocked', handler);
    });
    accountLockHandler.listeners.clear();
  }
};

export const dashboardService = {
  getCostAnalytics: async ({ fromDate, endDate }) => {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (endDate) params.append('end_date', endDate);
    const query = params.toString();
    const response = await api.get(`/api/dashboard/get-data${query ? `?${query}` : ''}`);
    return response.data;
  }
};

export const workOrderService = {
  // Get work orders with pagination
  getWorkOrdersByPagination: async (pageNumber = 1, pageSize = 10) => {
    const response = await api.get(`/api/maintenance-work-order/get-work-order-by-pagination/${pageNumber}/${pageSize}`);
    return response.data;
  },

  // Get single work order by work order number
  getWorkOrder: async (workOrderNumber) => {
    const response = await api.get(`/api/maintenance-work-order/get-work-order/${workOrderNumber}`);
    return response.data;
  },

  // Get work orders by status
  getWorkOrdersByStatus: async (status) => {
    const response = await api.get(`/api/maintenance-work-order/get-work-orders-by-status/${status}`);
    return response.data;
  },

  // Get work orders by priority
  getWorkOrdersByPriority: async (priority) => {
    const response = await api.get(`/api/maintenance-work-order/get-work-orders-by-priority/${priority}`);
    return response.data;
  },

  // Get work orders by due date range
  getWorkOrdersByDateRange: async (startDate, endDate) => {
    const response = await api.get(`/api/maintenance-work-order/get-work-orders-by-due-date-range/${startDate}/${endDate}`);
    return response.data;
  },

  // Get work orders metadata (counts)
  getWorkOrdersMetadata: async () => {
    const response = await api.get('/api/maintenance-work-order/get-work-orders-metadata');
    return response.data;
  },

  // Create work order
  createWorkOrder: async (workOrderData) => {
    const response = await api.post('/api/maintenance-work-order/create-work-order', workOrderData);
    return response.data;
  },

  // Update work order
  updateWorkOrder: async (workOrderNumber, workOrderData) => {
    const response = await api.put(`/api/maintenance-work-order/update-work-order/${workOrderNumber}`, workOrderData);
    return response.data;
  },

  // Update asset checklist for a work order
  updateAssetChecklist: async (workOrderNumber, assetId, checklist) => {
    const response = await api.put(
      `/api/maintenance-work-order/update-work_order-asset-checklist/${workOrderNumber}/${assetId}`,
      checklist
    );
    return response.data;
  },

  // Get dropdown options
  getStatuses: async () => {
    const response = await api.get('/api/maintenance-work-order/get-statuses');
    return response.data;
  },

  getPriorities: async () => {
    const response = await api.get('/api/maintenance-work-order/priorities');
    return response.data;
  },

  getWorkOrderTypes: async () => {
    const response = await api.get('/api/maintenance-work-order/work-order-types');
    return response.data;
  },

  getFailureCodes: async () => {
    const response = await api.get('/api/maintenance-work-order/failure-codes');
    return response.data;
  },

  getMillSections: async () => {
    const response = await api.get('/api/maintenance-work-order/mill-sections');
    return response.data;
  },

  getLocationAreas: async () => {
    const response = await api.get('/api/maintenance-work-order/location-area');
    return response.data;
  },

  getSchedulingWindows: async () => {
    const response = await api.get('/api/maintenance-work-order/get-scheduling-windows');
    return response.data;
  },

  getAssetConditions: async () => {
    const response = await api.get('/api/maintenance-work-order/get-asset-conditions');
    return response.data;
  },

  // Get user by name or email (for technician search)
  getUserByNameOrEmail: async (nameOrEmail) => {
    const response = await api.get(`/api/maintenance-work-order/get-user-by-name-or-email/${nameOrEmail || 'all'}`);
    return response.data;
  },

  // Attachments
  getWorkOrderAttachments: async (workOrderNumber) => {
    const response = await api.get(`/api/maintenance-work-order/work-order-attachments/${workOrderNumber}`);
    return response.data;
  },

  addWorkOrderAttachment: async (workOrderNumber, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/api/maintenance-work-order/add-work-order-attachment/${workOrderNumber}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  downloadAttachment: async (attachmentId) => {
    const response = await api.get(
      `/api/maintenance-work-order/download-attachment/${attachmentId}`,
      { responseType: 'blob' }
    );
    return response;
  },

  // Changesets
  getWorkOrderChangesets: async (workOrderNumber) => {
    const response = await api.get(`/api/maintenance-work-order/work-order-changesets/${workOrderNumber}`);
    return response.data;
  },

  // Approvals
  getWorkOrdersForApproval: async (pageNumber = 1, pageSize = 1000) => {
    const response = await api.get(`/api/maintenance-work-order/get-work-orders-for-approval/${pageNumber}/${pageSize}`);
    return response.data;
  },

  approveWorkOrders: async (workOrderNumbers, approvalTypes = []) => {
    const response = await api.post('/api/maintenance-work-order/approve-work-order', {
      work_order_numbers: workOrderNumbers,
      approval_types: approvalTypes
    });
    return response.data;
  },

  getOverdueWorkOrders: async () => {
    const response = await api.get('/api/maintenance-work-order/get-overdue-work-orders');
    return response.data;
  }
};

export const maintenanceService = {
  getDashboardSummary: async (date) => {
    const params = date ? `?date=${date}` : '';
    const response = await api.get(`/api/maintenance/dashboard-summary${params}`);
    return response.data;
  },

  getAssetHealth: async () => {
    const response = await api.get('/api/maintenance/asset-health');
    return response.data;
  },

  getWorkOrders: async (status, priority, limit = 50) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    params.append('limit', limit);
    const response = await api.get(`/api/maintenance/work-orders?${params}`);
    return response.data;
  },

  getPMSchedule: async (status, daysAhead = 30) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('days_ahead', daysAhead);
    const response = await api.get(`/api/maintenance/pm-schedule?${params}`);
    return response.data;
  },

  getAlerts: async (status = 'active') => {
    const response = await api.get(`/api/maintenance/alerts?status=${status}`);
    return response.data;
  },

  getBreakdowns: async (days = 30, sectionId) => {
    const params = new URLSearchParams();
    params.append('days', days);
    if (sectionId) params.append('section_id', sectionId);
    const response = await api.get(`/api/maintenance/breakdowns?${params}`);
    return response.data;
  },

  getSpareParts: async (criticalOnly = false, status) => {
    const params = new URLSearchParams();
    if (criticalOnly) params.append('critical_only', 'true');
    if (status) params.append('status', status);
    const response = await api.get(`/api/maintenance/spare-parts?${params}`);
    return response.data;
  },

  getCostBreakdown: async (month) => {
    const params = month ? `?month=${month}` : '';
    const response = await api.get(`/api/maintenance/costs${params}`);
    return response.data;
  }
};

export const assetService = {
  // Get all assets with open work order counts
  getAllAssets: async () => {
    const response = await api.get('/api/maintenance-assets/get-all-assets');
    return response.data;
  },

  // Get single asset by ID
  getAssetById: async (assetId) => {
    const response = await api.get(`/api/maintenance-assets/get-asset/${assetId}`);
    return response.data;
  },

  // Get assets by name (partial match)
  getAssetByName: async (assetName) => {
    const response = await api.get(`/api/maintenance-assets/get-asset-by-name/${encodeURIComponent(assetName)}`);
    return response.data;
  },

  // Get assets by parent category
  getAssetsByParent: async (parentAsset) => {
    const response = await api.get(`/api/maintenance-assets/get-asset-by-parent/${encodeURIComponent(parentAsset)}`);
    return response.data;
  },

  // Get all parent asset categories
  getParentAssets: async () => {
    const response = await api.get('/api/maintenance-assets/get-all-parent-assets');
    return response.data;
  },

  // Get asset criticality options
  getCriticalities: async () => {
    const response = await api.get('/api/maintenance-assets/get-asset-criticalities');
    return response.data;
  },

  // Create new asset
  createAsset: async (assetData) => {
    const response = await api.post('/api/maintenance-assets/create-asset', assetData);
    return response.data;
  },

  // Update existing asset (partial update — only changed fields are sent)
  updateAsset: async (id, assetData) => {
    const response = await api.put(`/api/maintenance-assets/update-asset/${id}`, assetData);
    return response.data;
  },

  // Get mill sections (locations)
  getMillSections: async () => {
    const response = await api.get('/api/maintenance-work-order/mill-sections');
    return response.data;
  },

  // Get location areas
  getLocationAreas: async () => {
    const response = await api.get('/api/maintenance-work-order/location-area');
    return response.data;
  }
};

export const pmScheduleService = {
  // Create PM Schedule
  createPMSchedule: async (pmScheduleData) => {
    const response = await api.post('/api/pm-master/', pmScheduleData);
    return response.data;
  },

  // Update PM Schedule
  updatePMSchedule: async (pmId, pmScheduleData) => {
    const response = await api.put(`/api/pm-master/${pmId}`, pmScheduleData);
    return response.data;
  },

  // Get PM Schedule by ID
  getPMScheduleById: async (pmScheduleId) => {
    const response = await api.get(`/api/pm-master/id/${pmScheduleId}`);
    return response.data;
  },

  // Get PM Schedules with pagination and filters
  getPMSchedulesByPagination: async (page = 1, size = 10, filters = {}) => {
    const params = new URLSearchParams({ page, size });
    if (filters.asset) params.append('asset', filters.asset);
    if (filters.shutdown !== undefined && filters.shutdown !== null) params.append('shutdown', filters.shutdown);
    if (filters.frequency) params.append('frequency', filters.frequency);
    if (filters.team) params.append('team', filters.team);
    const response = await api.get(`/api/pm-master/?${params.toString()}`);
    return response.data;
  },

  // Get PM Schedules by date range (for calendar)
  getPMSchedulesByDateRange: async (startDate, endDate) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    const response = await api.get(`/api/pm-master/by-date-range?${params.toString()}`);
    return response.data;
  },

  // Get PM Schedule metadata (counts, distributions)
  getPMScheduleMetadata: async () => {
    const response = await api.get('/api/pm-master/metadata');
    return response.data;
  },

  // Stage selected PM items as TEMP (persisted to DB)
  stageTempWorkOrders: async (pmIds, startDate, endDate) => {
    const response = await api.post('/api/pm-master/stage-temp', {
      pm_ids: pmIds,
      start_date: startDate,
      end_date: endDate
    });
    return response.data;
  },

  // Revert TEMP items back to PLANNED
  unstageTempWorkOrders: async (pmIds, startDate, endDate) => {
    const response = await api.post('/api/pm-master/unstage-temp', {
      pm_ids: pmIds,
      start_date: startDate,
      end_date: endDate
    });
    return response.data;
  },

  // Generate work orders from PM schedules (only converts staged TEMP items)
  generateWorkOrders: async (pmIds, startDate, endDate) => {
    const response = await api.post('/api/pm-master/generate-workorders', {
      pm_ids: pmIds,
      start_date: startDate,
      end_date: endDate
    });
    return response.data;
  }
};

export default api;
